import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API, fmtErr } from "../lib/pos";

export function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("signin"); // "signin" | "forgot"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  // Auth-hardening state
  const [captcha, setCaptcha] = useState(null); // {challenge_id, image_data_uri}
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [blockedUntil, setBlockedUntil] = useState(0); // epoch-ms
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (!blockedUntil) { setCountdown(0); return; }
    const tick = () => {
      const remain = Math.max(0, Math.ceil((blockedUntil - Date.now()) / 1000));
      setCountdown(remain);
      if (remain === 0) setBlockedUntil(0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [blockedUntil]);

  const refreshCaptcha = async (targetEmail) => {
    try {
      const r = await axios.get(`${API}/auth/captcha-challenge`, { params: { email: targetEmail || email } });
      setCaptcha(r.data);
      setCaptchaAnswer("");
      return true;
    } catch (ex) {
      // If the endpoint is missing (e.g. an older backend deploy), tell the user
      // instead of silently swallowing — otherwise they see the login stuck in
      // "please solve the security check" with no captcha shown.
      const status = ex?.response?.status;
      if (status === 404) {
        setErr("Security check service is unavailable. Please refresh the page or contact support.");
      } else {
        setErr("Couldn't load the security check. Please try again.");
      }
      return false;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (countdown > 0) return;
    setBusy(true); setErr("");
    try {
      await login(email, password, {
        remember_me: rememberMe,
        captcha_id: captcha?.challenge_id,
        captcha_answer: captcha ? captchaAnswer : undefined,
      });
      // Reset captcha on success
      setCaptcha(null); setCaptchaAnswer("");
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : detail;
      if (status === 428 || code === "captcha_required") {
        setErr("Please solve the security check to continue.");
        await refreshCaptcha(email);
      } else if (status === 429 || code === "login_temporarily_blocked") {
        const secs = (typeof detail === "object" && detail?.retry_after_seconds) ||
                     parseInt(ex?.response?.headers?.["retry-after"] || "900", 10);
        setBlockedUntil(Date.now() + secs * 1000);
        setErr(`Too many failed attempts. Try again in ${Math.ceil(secs / 60)} minute${secs > 60 ? "s" : ""}.`);
        setCaptcha(null); setCaptchaAnswer("");
      } else {
        setErr(fmtErr(ex));
        // If a captcha was in play, refresh it so a new challenge is issued.
        if (captcha) await refreshCaptcha(email);
      }
    } finally { setBusy(false); }
  };
  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true); setForgotMsg("");
    try {
      const r = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotMsg(r.data?.message || "If that email is registered, a reset link has been sent.");
    } catch (ex) { setForgotMsg(fmtErr(ex)); }
    finally { setForgotBusy(false); }
  };
  const year = new Date().getFullYear();
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <img src="/logo.jpg" alt="Shimla QoH Food Court" className="auth-logo" />
          <div>Shimla QoH<small>FOOD COURT · POS</small></div>
        </div>
        {mode === "signin" ? (
          <>
            <p className="eyebrow" style={{ marginTop: 22 }}>SIGN IN</p>
            <h1>Welcome to Shimla QoH Food Court</h1>
            <p className="muted" style={{ marginTop: 6 }}>Managers oversee every station. Station accounts see only their tickets and reports.</p>
            {/* autocomplete="off" + a randomised name + hidden decoy inputs = Chrome/Edge/Safari
                will not offer the saved-credential dropdown on this form */}
            <form onSubmit={submit} className="auth-form" autoComplete="off">
              <input type="text" name="prevent-autofill" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />
              <input type="password" name="prevent-autofill-pw" style={{ display: "none" }} tabIndex={-1} autoComplete="new-password" />
              <label>Email
                <input
                  data-testid="login-email-input"
                  type="email"
                  name={`email_${Math.random().toString(36).slice(2, 8)}`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                />
              </label>
              <label>Password
                <input
                  data-testid="login-password-input"
                  type="password"
                  name={`password_${Math.random().toString(36).slice(2, 8)}`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                />
              </label>
              {captcha && (
                <div className="auth-captcha" data-testid="auth-captcha">
                  <label style={{ marginBottom: 4 }}>Security check
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                      <img
                        src={captcha.image_data_uri}
                        alt="Security check"
                        data-testid="captcha-image"
                        style={{ height: 48, borderRadius: 6, border: "1px solid var(--line)", background: "#eef2f7" }}
                      />
                      <button
                        type="button"
                        onClick={() => refreshCaptcha(email)}
                        data-testid="captcha-refresh"
                        title="New challenge"
                        style={{ padding: "6px 10px", fontSize: 11, background: "none", border: "1px solid var(--line)", color: "var(--muted)", borderRadius: 6, cursor: "pointer" }}
                      >↻</button>
                    </div>
                  </label>
                  <input
                    data-testid="captcha-input"
                    type="text"
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value.toUpperCase())}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={8}
                    placeholder="Type the characters"
                    required
                    style={{ letterSpacing: 4, textTransform: "uppercase" }}
                  />
                </div>
              )}
              <label
                className="auth-remember"
                style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--muted)", cursor: "pointer", flexDirection: "row", marginTop: 4 }}
              >
                <input
                  data-testid="remember-me-checkbox"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ margin: 0 }}
                />
                <span>Remember me on this device</span>
              </label>
              {err && <div data-testid="login-error" className="auth-error">{err}</div>}
              <button
                data-testid="login-submit-button"
                className="primary wide"
                disabled={busy || countdown > 0}
                type="submit"
              >{countdown > 0
                ? `Locked · retry in ${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`
                : (busy ? "Signing in..." : "Sign in")}</button>
            </form>
            <div style={{ marginTop: 14, textAlign: "right" }}>
              <button
                data-testid="forgot-password-link"
                type="button"
                onClick={() => { setMode("forgot"); setErr(""); setForgotEmail(email); setForgotMsg(""); }}
                style={{ background: "none", color: "var(--muted)", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", padding: 0 }}
              >Forgot password?</button>
            </div>
          </>
        ) : (
          <>
            <p className="eyebrow" style={{ marginTop: 22 }}>RESET PASSWORD</p>
            <h1>Forgot your password?</h1>
            <p className="muted" style={{ marginTop: 6 }}>Enter the email you use to sign in. We'll email you a link to set a new password.</p>
            <form onSubmit={submitForgot} className="auth-form" autoComplete="off">
              <label>Email
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  name={`forgot_${Math.random().toString(36).slice(2, 8)}`}
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  autoComplete="off"
                  required
                />
              </label>
              {forgotMsg && (
                <div data-testid="forgot-message" className={forgotMsg.toLowerCase().includes("sent") ? "auth-success" : "auth-error"}>
                  {forgotMsg}
                </div>
              )}
              <button
                data-testid="forgot-submit-button"
                className="primary wide"
                disabled={forgotBusy}
                type="submit"
              >{forgotBusy ? "Sending…" : "Email me a reset link"}</button>
            </form>
            <div style={{ marginTop: 14, textAlign: "right" }}>
              <button
                data-testid="back-to-signin-link"
                type="button"
                onClick={() => { setMode("signin"); setForgotMsg(""); }}
                style={{ background: "none", color: "var(--muted)", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", padding: 0 }}
              >← Back to sign in</button>
            </div>
          </>
        )}
      </div>
      <div className="app-copyright" data-testid="copyright-footer">
        Copyright {year}: KKK Food Court Pvt Ltd
      </div>
    </div>
  );
}
